import {
  biTimeRangeLabel,
  kBiCardOwnRangeLabel,
} from '@euler/app/components/message/widgets/bi-visualization/functions/biTimeRangeLabel';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDashboardFilters } from '@euler/model/bi/dashboard';
import {
  BiRelativeTimePreset,
  BiTimeRange,
  BiTimeRangeKind,
} from '@euler/model/bi/query';
import { CalendarRange } from 'lucide-react-native';
import { ReactNode, memo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { BiChip } from '../../components/ui/BiChip';
import {
  BiDateChoice,
  biEffectiveRange,
  biSameTimeRange,
  kBiDateRangeOptions,
} from '../functions/biDateRange';
import { DateRangePanel } from '../time-range/DateRangePanel';

const relative = (preset: BiRelativeTimePreset): BiTimeRange => ({
  kind: BiTimeRangeKind.Relative,
  preset,
});

/**
 * The dashboard's date range: it overrides each card's own time range. A
 * pick here is the viewer's own for this visit; the saved one applies
 * until then.
 */
export const DateRangeBar = memo(
  ({
    choice,
    saved,
    onChange,
    trailing,
  }: {
    choice: BiDateChoice;
    saved: BiDashboardFilters;
    onChange: (choice: BiDateChoice) => void;
    /** e.g. saving the pick as the dashboard's own. */
    trailing?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const [custom, setCustom] = useState(false);
    const current = biEffectiveRange(choice, saved);
    const pick = (range: BiTimeRange | undefined) =>
      onChange(
        biSameTimeRange(range, saved.dateRange)
          ? { kind: 'saved' }
          : { kind: 'override', range },
      );
    const listed = (range: BiTimeRange | undefined) =>
      !range ||
      kBiDateRangeOptions.some(option =>
        biSameTimeRange(relative(option.preset), range),
      );
    const savedIsListed = listed(saved.dateRange);
    // A rolling or named range picked here, not the saved one.
    const ownCustom =
      !listed(current) && !biSameTimeRange(current, saved.dateRange);
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}>
        <CalendarRange size={16} color={aiColors.mutedForeground} />
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flex: 1 }}
          contentContainerStyle={{ gap: rpx(6), alignItems: 'center' }}
        >
          <BiChip
            label={kBiCardOwnRangeLabel}
            selected={!current}
            onPress={() => pick(undefined)}
          />
          {!savedIsListed ? (
            <BiChip
              label={biTimeRangeLabel(saved.dateRange)}
              selected={biSameTimeRange(current, saved.dateRange)}
              onPress={() => pick(saved.dateRange)}
            />
          ) : null}
          {kBiDateRangeOptions.map(option => {
            const range = relative(option.preset);
            return (
              <BiChip
                key={option.preset}
                label={option.label}
                selected={biSameTimeRange(current, range)}
                onPress={() => pick(range)}
              />
            );
          })}
          <BiChip
            label={ownCustom ? biTimeRangeLabel(current) : '自定义…'}
            selected={ownCustom}
            onPress={() => setCustom(true)}
          />
        </ScrollView>
        {trailing}
        <DateRangePanel
          visible={custom}
          value={current}
          onPick={pick}
          onClose={() => setCustom(false)}
        />
      </View>
    );
  },
);
